import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/**
 * Título, descripción, canonical, Open Graph y datos estructurados de cada página pública.
 *
 * Funciona igual en el navegador y en el prerender del build (usa DOCUMENT, no `window`),
 * así que lo que se setea acá queda escrito en el HTML que recibe Google y en la vista
 * previa de WhatsApp/Facebook, que no ejecutan JavaScript.
 */
export const SITE_URL = 'https://auracentromedico.com';
const SITE_NAME = 'Aura';
const IMAGEN_DEFAULT = `${SITE_URL}/assets/images/og-aura.png`;

export interface SeoPagina {
  titulo: string;
  descripcion: string;
  /** Path absoluto dentro del sitio, p. ej. `/marketplace/barberia-don-ramon`. */
  path: string;
  imagen?: string | null;
  tipo?: 'website' | 'article';
  /** Un objeto schema.org, o varios. Se serializa en un único `<script type=ld+json>`. */
  jsonLd?: object | object[] | null;
  /** Páginas que no deben indexarse (cuenta, formularios con sesión). */
  noindex?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  set(p: SeoPagina): void {
    const titulo = p.titulo.includes(SITE_NAME) ? p.titulo : `${p.titulo} · ${SITE_NAME}`;
    const descripcion = recortar(p.descripcion, 160);
    const url = `${SITE_URL}${p.path}`;
    const imagen = p.imagen || IMAGEN_DEFAULT;

    this.title.setTitle(titulo);
    this.meta.updateTag({ name: 'description', content: descripcion });
    this.meta.updateTag({ name: 'robots', content: p.noindex ? 'noindex, follow' : 'index, follow' });

    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ property: 'og:locale', content: 'es_PE' });
    this.meta.updateTag({ property: 'og:type', content: p.tipo ?? 'website' });
    this.meta.updateTag({ property: 'og:title', content: titulo });
    this.meta.updateTag({ property: 'og:description', content: descripcion });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: imagen });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });

    this.canonical(url);
    this.jsonLd(p.jsonLd ?? null);
  }

  private canonical(url: string): void {
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private jsonLd(data: object | object[] | null): void {
    const previo = this.doc.getElementById('seo-jsonld');
    if (previo) previo.remove();
    if (!data) return;
    const s = this.doc.createElement('script');
    s.id = 'seo-jsonld';
    s.setAttribute('type', 'application/ld+json');
    // `<` escapado: un nombre de negocio con "</script>" no puede cerrar la etiqueta.
    s.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
    this.doc.head.appendChild(s);
  }
}

function recortar(texto: string, max: number): string {
  const t = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const corte = t.slice(0, max - 1);
  return `${corte.slice(0, corte.lastIndexOf(' ') > 80 ? corte.lastIndexOf(' ') : corte.length)}…`;
}

/**
 * Tipo schema.org según el rubro. Google usa el tipo para entender qué es el negocio; uno
 * más específico que `LocalBusiness` ayuda en búsquedas como "veterinaria cerca".
 */
export function tipoSchemaPorRubro(rubro?: string | null): string {
  switch ((rubro ?? '').toLowerCase()) {
    case 'veterinaria':
      return 'VeterinaryCare';
    case 'dentista':
      return 'Dentist';
    case 'spa':
      return 'DaySpa';
    case 'barberia':
      return 'HairSalon';
    case 'manicura':
      return 'NailSalon';
    case 'diagnostico':
      return 'DiagnosticLab';
    case 'clinica':
    case 'estetica':
      return 'MedicalClinic';
    default:
      return 'LocalBusiness';
  }
}
